import React from "react";
import {Drawer, Tabs} from "antd";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {Nullable} from "@data-force/app";
import StoreState from "../../interfaces/store/storeState";
import "./DetailsDrawer.scss";
import {DestroyDetailsDrawerAction} from "../../interfaces/store/actions/job";
import {destroyDetailsDrawer} from "../../store/actions/job";
import TaskList from "./TaskList/TaskList";
import TaskDetails from "../TaskDetails/TaskDetails";
import TaskDirections from "../TaskDirections/TaskDirections";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {ContentType} from "../../enums/enums";
import {getLocalePrefix, setFocusedElement} from "../../utils/common";
import {DrawerTabs} from "../../utils/constants";
import JobTitle from "../common/JobTitle/JobTitle";

interface StateProps {
    visible: boolean
    current: JobEntity
    activeJobType: string
    translate: TranslateFunction
    isMobile: boolean
    isTest: Nullable<boolean>
    activeTab: string
}

interface DispatchProps {
    destroyDetailsDrawer(): DestroyDetailsDrawerAction
}

type Props = StateProps & DispatchProps;

const DetailsDrawer: React.FC<Props> = (props: Props): React.ReactElement => {
    const {
        visible,
        isMobile,
        activeJobType,
        translate,
        current: {
            name = "", title = "", longDescriptionRefId, projectType,
        },
        activeTab,
        isTest,
    }: Props = props;
    const translatePath: string = "components.DetailsDrawer.DetailsDrawer";
    const {IN_PROGRESS, COMPLETED}: typeof ContentType = ContentType;
    const {DIRECTIONS, DETAILS, STATS}: typeof DrawerTabs = DrawerTabs;
    const localePrefix: string = getLocalePrefix(isTest);

    /**
     * Prevent context menu opening on long press only on mobile
     * @param {React.MouseEvent} e
     */
    const preventContextMenu = (e:React.MouseEvent): void => {
        if (isMobile) {
            e.preventDefault();
        }
    };

    /**
     * Closing Details Drawer and restoring focus to last focused list item
     */
    const onCloseHandler = (): void => {
        props.destroyDetailsDrawer();
        setFocusedElement();
    };

    return (
        <div onContextMenu={preventContextMenu}>
            <Drawer
                className="df-details-drawer"
                destroyOnClose
                onClose={onCloseHandler}
                maskClosable
                visible={visible}
                title={<JobTitle {...{translate, title, phaseName: name}} tooltipRequired tooltipPlacement="bottomLeft"/>}
            >
                <Tabs
                    type="card"
                    defaultActiveKey={activeTab}
                    moreIcon={(
                        <span aria-label={translate(`${translatePath}.moreTabs.text`) as string}>
                            <Translate id={`${translatePath}.moreTabsContent.text`}/>
                        </span>
                    )}
                >
                    {!isTest && ((activeJobType === IN_PROGRESS || activeJobType === COMPLETED) && (
                        <Tabs.TabPane
                            key={STATS}
                            tab={translate(`${translatePath}.taskStatusTabTitle.text`) as string}
                        >
                            <TaskList/>
                        </Tabs.TabPane>
                    ))}
                    <Tabs.TabPane
                        key={DETAILS}
                        tab={translate(`${translatePath}.${localePrefix}DetailsTabTitle.text`) as string}
                    >
                        <TaskDetails key="details-drawer"/>
                    </Tabs.TabPane>
                    {!!longDescriptionRefId && isTest && (
                        <Tabs.TabPane
                            key={DIRECTIONS}
                            tab={translate(`${translatePath}.taskDirectionsTabTitle.text`) as string}
                        >
                            <div className="df-directions-container">
                                <TaskDirections projectType={projectType}/>
                            </div>
                        </Tabs.TabPane>
                    )}
                </Tabs>
            </Drawer>
        </div>
    );
};

function mapStateToProps(state: StoreState): StateProps {
    return {
        visible: state.job.isDetailsDrawerVisible,
        current: state.job.selectedJob || {} as JobEntity,
        translate: getTranslate(state.localize),
        activeJobType: state.job.activeJobType,
        isMobile: state.mobile.isMobile,
        isTest: state.job.isTest,
        activeTab: state.job.activeDetailsDrawerTab,
    };
}

const mapDispatchToProps: DispatchProps = {
    destroyDetailsDrawer,
};

export default connect(mapStateToProps, mapDispatchToProps)(DetailsDrawer);
